import {
  getUserFacingApiErrorMessage,
} from "../services/api";
import {
  getVaultFieldErrors,
  getVaultSecretRequestDetails,
  submitVaultSecrets,
  type VaultSecretRequestDetails,
} from "../services/vaultApi";
import {
  SecretEntryPage
} from "./pairing/SecretEntryPage";
import {
  baseFieldKey,
  companionFieldKey,
  formFieldKey,
  prefixValues,
  splitVaultValues,
} from "./vault/companionValues";
import {
  PassportScanSection
} from "./vault/PassportScanSection";

// Fields the passport MRZ can fill. The scanner is offered when a section
// asks for at least two of them — a single overlapping field (say, just
// gender) isn't worth pointing a camera at a passport for.
const PASSPORT_SCANNABLE_KEYS = new Set([
  "passport_number",
  "passport_expiry",
  "legal_first_name",
  "legal_last_name",
  "date_of_birth",
  "gender",
]);

// The account holder's own section, present only when there is someone else's
// to tell it apart from. Its id is never a companion subject.
const OWN_SECTION = { id: "self", title: "Your details" };

type VaultRequestField = VaultSecretRequestDetails["fields"][number];

export function VaultEnterSecretPage() {
  // ISO date (YYYY-MM-DD) for "today" in the user's locale. It bounds both
  // kinds of date field, from opposite sides: a date of birth cannot be in the
  // future, a travel document's expiry cannot be in the past.
  const todayIso = new Date().toLocaleDateString("en-CA");
  const toField = (field: VaultRequestField, phoneRegion: string | null) => {
    const isPastDate = field.value_type === "date";
    const isFutureDate = field.value_type === "future_date";
    const isPhone = field.value_type === "phone";
    return {
      key: field.key,
      label: field.label,
      helpText: field.description ?? null,
      sensitive: field.sensitive,
      ...(field.options ? { options: field.options } : {}),
      ...(isPastDate ? { inputType: "date" as const, max: todayIso } : {}),
      ...(isFutureDate ? { inputType: "date" as const, min: todayIso } : {}),
      ...(isPhone
        ? { inputType: "phone" as const, defaultRegion: phoneRegion }
        : {}),
    };
  };
  return (
    <SecretEntryPage
      loadDetails={async (token) => {
        const res = await getVaultSecretRequestDetails(token);
        // The frontend goes live minutes before the backend's rollout
        // finishes; in that window the API still answers without
        // `companions`, and this page is what a user mid-booking is told to
        // open right now — so an absent list is an empty one, not a dead form.
        const companions = res.companions ?? [];
        const hasCompanions = companions.length > 0;
        // The owner's region, so a number typed the way they'd say it is
        // read with their country code (GET-15087). Absent during the same
        // rollout window as `companions`, when the picker just starts empty.
        const phoneRegion = res.phone_region ?? null;
        return {
          title: "Securely save your details",
          fields: [
            ...res.fields.map((field) => ({
              ...toField(field, phoneRegion),
              ...(hasCompanions ? { section: OWN_SECTION } : {}),
            })),
            // One section per travelling companion, identified by their vault
            // subject. Their fields are the same catalog fields, so the form
            // key carries the subject and is split back out on submit.
            ...companions.flatMap((companion) =>
              companion.fields.map((field) => ({
                ...toField(field, phoneRegion),
                key: companionFieldKey(companion.subject, field.key),
                section: { id: companion.subject, title: companion.label },
              })),
            ),
          ],
        };
      }}
      submit={(token, flat) => {
        const { values, companion_values } = splitVaultValues(flat);
        return submitVaultSecrets(token, values, companion_values);
      }}
      mapLoadError={(err) =>
        getUserFacingApiErrorMessage(err, "Failed to load this request")
      }
      mapSaveError={(err) =>
        getUserFacingApiErrorMessage(err, "Failed to save details")
      }
      mapFieldErrors={(err) =>
        Object.fromEntries(
          getVaultFieldErrors(err).map((e) => [
            formFieldKey(e.subject, e.key),
            e.message,
          ]),
        )
      }
      description="Enter the details below. They're encrypted at rest. Sensitive ones, like your passport number, are never shown to the assistant — it fills them into the right form field without seeing them. Basic ones, like your nationality, gender, city or passport expiry date, are visible to it while it fills a form, so it can pick the right option from a site's list."
      submitLabel="Save securely"
      doneBody="Your details are saved securely. You can close this tab and continue the conversation with the assistant."
      prefillSlot={(applyValues, fields, sectionId) => {
        if (
          fields.filter((f) => PASSPORT_SCANNABLE_KEYS.has(baseFieldKey(f.key)))
            .length < 2
        ) {
          return null;
        }
        // A scan lands on the section that offered it: re-keyed onto the
        // companion's subject, or left as-is for the account holder.
        const subject =
          sectionId === null || sectionId === OWN_SECTION.id ? null : sectionId;
        return (
          <PassportScanSection
            onApply={(values) => applyValues(prefixValues(values, subject))}
          />
        );
      }}
    />
  );
}
